import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import SprekersLayout from '@/components/SprekersLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { sprekersApi } from '../api';
import SectionHeader from '../components/SectionHeader';
import type { CanvasTemplateSummary } from '../types';

/** Beheerpagina voor canvas-templates -- mirrort TemplatesPage.tsx (de
 * bestaande PresentationTemplate-pagina) qua opzet: lijst + verwijderen.
 * Ingebouwde templates zijn nooit verwijderbaar (geen prullenbak-knop). */
export default function CanvasTemplatesPage() {
  const [templates, setTemplates] = useState<CanvasTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = async () => {
    try {
      const data = await sprekersApi.listCanvasTemplates();
      setTemplates(data);
    } catch (error) {
      toast.error('Kon canvas-sjablonen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async (template: CanvasTemplateSummary) => {
    if (!window.confirm(`Weet je zeker dat je "${template.name}" wilt verwijderen?`)) return;
    try {
      await sprekersApi.deleteCanvasTemplate(template.id);
      toast.success('Sjabloon verwijderd');
      fetchTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  return (
    <SprekersLayout>
      <SectionHeader
        title="Canvas-sjablonen"
        description="Kant-en-klare en zelf opgeslagen herbruikbare structuren voor canvas-presentaties."
      />

      <Card>
        <CardContent className="p-0">
          {!loading && templates.length === 0 && (
            <p className="p-6 text-sm text-slate-500">Nog geen canvas-sjablonen.</p>
          )}
          <div className="divide-y divide-slate-100">
            {templates.map((template) => (
              <div key={template.id} className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900">{template.name}</p>
                    {template.is_builtin && <Badge className="bg-teal-100 text-teal-700">Ingebouwd</Badge>}
                  </div>
                  {template.description && <p className="mt-1 text-sm text-slate-500">{template.description}</p>}
                </div>
                {!template.is_builtin && (
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(template)} aria-label="Verwijderen">
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </SprekersLayout>
  );
}
